/**
 * Theme handling: resolving the account's three-state preference against the
 * system colour scheme, applying it to the document, and mirroring the result
 * to the one key the app is allowed to keep in the browser (research §22).
 *
 * The authoritative preference is per-account and arrives with the session
 * (FR-056). `viewBuilder.themeHint` exists only so that `index.html` can set
 * `data-theme` before first paint; it is the sole browser storage entry the
 * app writes (Principle I).
 *
 * @author John Grimes
 */

import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
} from "react";

import type { ThemePreference } from "shared/api";

/** A theme that can actually be applied, once the system is taken into account. */
export type ResolvedTheme = "light" | "dark";

/** The only key the app writes to browser storage. */
export const THEME_HINT_KEY = "viewBuilder.themeHint";

/** The media query that reports the operating system's colour scheme. */
const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/**
 * Resolves the account's preference against the system colour scheme.
 *
 * @param preference - The account's preference: system, light or dark.
 * @param systemPrefersDark - Whether the system currently asks for dark.
 * @returns The theme to apply.
 *
 * @example
 * ```ts
 * resolveTheme("system", true); // "dark"
 * resolveTheme("light", true); // "light"
 * ```
 */
export const resolveTheme = (
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme => {
  if (preference === "system") {
    return systemPrefersDark ? "dark" : "light";
  }
  return preference;
};

/**
 * Reads the stored theme hint, tolerating storage that is absent, blocked or
 * holding something the app did not write.
 *
 * @returns The stored theme, or `undefined` when there is no usable hint.
 */
export const readThemeHint = (): ResolvedTheme | undefined => {
  try {
    const stored = localStorage.getItem(THEME_HINT_KEY);
    return stored === "light" || stored === "dark" ? stored : undefined;
  } catch {
    // Storage can be unavailable; the system preference is a fine fallback.
    return undefined;
  }
};

/**
 * Mirrors the resolved theme into browser storage, so the next first paint
 * matches before the session has loaded.
 *
 * @param theme - The theme that is now in force.
 */
export const writeThemeHint = (theme: ResolvedTheme): void => {
  try {
    localStorage.setItem(THEME_HINT_KEY, theme);
  } catch {
    // A missing hint only costs a flash on the next load.
  }
};

/**
 * Applies a theme to the document and records it for the next first paint.
 *
 * @param theme - The theme to apply.
 *
 * @example
 * ```ts
 * applyTheme(resolveTheme(preference, prefersDarkColourScheme()));
 * ```
 */
export const applyTheme = (theme: ResolvedTheme): void => {
  document.documentElement.dataset.theme = theme;
  writeThemeHint(theme);
};

/**
 * The preference to start from before the account's settings arrive. A stored
 * hint is the closest thing to the account's own choice; without one the app
 * follows the system.
 *
 * @returns The starting preference.
 */
export const initialThemePreference = (): ThemePreference =>
  readThemeHint() ?? "system";

/**
 * Reports whether the operating system currently asks for a dark interface.
 *
 * @returns True when the system prefers dark, false when it prefers light or
 *   cannot say.
 */
export const prefersDarkColourScheme = (): boolean =>
  typeof matchMedia === "function" && matchMedia(DARK_SCHEME_QUERY).matches;

/**
 * Watches the operating system's colour scheme.
 *
 * @param onChange - Called whenever the system preference changes.
 * @returns A function that stops watching.
 */
export const subscribeToColourScheme = (onChange: () => void): (() => void) => {
  // Not every environment implements media queries; watching is then a no-op.
  const query =
    typeof matchMedia === "function"
      ? matchMedia(DARK_SCHEME_QUERY)
      : undefined;
  query?.addEventListener("change", onChange);
  return () => {
    query?.removeEventListener("change", onChange);
  };
};

/**
 * Resolves and applies the theme, keeping up with the system while the
 * preference is `system`.
 *
 * @param preference - The account's preference.
 * @returns The theme currently in force.
 */
export const useResolvedTheme = (
  preference: ThemePreference,
): ResolvedTheme => {
  // Watching the system unconditionally is simpler than subscribing and
  // unsubscribing as the preference changes, and only `system` reads the value.
  const systemPrefersDark = useSyncExternalStore(
    subscribeToColourScheme,
    prefersDarkColourScheme,
    () => false,
  );
  const resolvedTheme = resolveTheme(preference, systemPrefersDark);
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);
  return resolvedTheme;
};

/** The theme as the rest of the app sees it. */
export type ThemeContextValue = {
  /** The account's three-state preference. */
  readonly preference: ThemePreference;
  /** What that preference resolves to right now. */
  readonly resolvedTheme: ResolvedTheme;
  /** Changes the preference; the account's settings follow separately. */
  readonly setPreference: (preference: ThemePreference) => void;
};

/** Carries the theme to the navbar toggle and the settings screen. */
export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
);

/**
 * Reads the theme from context.
 *
 * @returns The current theme and the function that changes it.
 * @throws {Error} When used outside the application shell, which always
 *   provides it.
 */
export const useTheme = (): ThemeContextValue => {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme was used outside the application shell");
  }
  return value;
};
